import React from 'react';
import { FaSync, FaPlus, FaExclamationTriangle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useResponsive } from '../../components/vm/useResponsive';

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Error message UI element
const ErrorMessage = ({ message }) => (
  <div className="bg-red-900 bg-opacity-20 p-4 rounded border border-red-800 text-red-200 mb-4">
    <div className="flex items-start">
      <FaExclamationTriangle className="mt-1 mr-2 flex-shrink-0" />
      <div>{sanitizeText(message)}</div>
    </div>
  </div>
);

// Pagination Component
const MobilePagination = ({ 
  currentPage, 
  pageCount, 
  onNextPage, 
  onPrevPage, 
  onPageChange 
}) => {
  if (pageCount <= 1) return null;
  
  return (
    <div className="flex justify-center items-center mt-4 mb-6 space-x-2">
      <button
        onClick={onPrevPage}
        disabled={currentPage === 1}
        className={`flex items-center justify-center w-8 h-8 rounded-full ${
          currentPage === 1 
            ? 'bg-[#222222] text-gray-500 cursor-not-allowed' 
            : 'bg-[#333333] text-white hover:bg-[#444444]'
        } shadow transition-colors`}
        aria-label="Previous page"
      >
        <FaChevronLeft size={14} />
      </button>
      
      <div className="flex space-x-1">
        {Array.from({ length: pageCount }).map((_, index) => (
          <button
            key={`page-${index}`}
            onClick={() => onPageChange(index + 1)}
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentPage === index + 1 
                ? 'bg-[#e0eacc] text-black font-medium' 
                : 'bg-[#333333] text-white hover:bg-[#444444]'
            } transition-colors`}
            aria-label={`Go to page ${index + 1}`}
          >
            {index + 1}
          </button>
        ))}
      </div>
      
      <button
        onClick={onNextPage}
        disabled={currentPage === pageCount}
        className={`flex items-center justify-center w-8 h-8 rounded-full ${
          currentPage === pageCount 
            ? 'bg-[#222222] text-gray-500 cursor-not-allowed' 
            : 'bg-[#333333] text-white hover:bg-[#444444]'
        } shadow transition-colors`}
        aria-label="Next page"
      >
        <FaChevronRight size={14} />
      </button>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, className = "", onClick, isSelected = false }) => (
  <div 
    className={`bg-[#1a1a1a] rounded-md p-3 border shadow-sm transition-colors ${
      onClick ? 'cursor-pointer hover:bg-[#222222]' : ''
    } ${
      isSelected ? 'border-[#e0eacc]' : 'border-[#333333]'
    } ${className}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-xs mb-1">{title}</p>
        <p className="text-lg font-bold text-white">{value}</p>
      </div>
      <div className={`p-2 rounded-full shadow-sm ${
        isSelected ? 'bg-[#1c2b22]' : 'bg-[#222222]'
      }`}>
        <Icon className={`${isSelected ? 'text-[#e0eacc]' : 'text-[#e0eacc]'}`} size={16} />
      </div>
    </div>
  </div>
);

const ResponsiveWrapper = ({
  // Page header props
  pageTitle,
  pageDescription,
  pageIcon: PageIcon,
  
  // Actions
  onRefresh,
  onCreateNew,
  createButtonText = "Create",
  isRefreshing = false,
  canCreate = true,
  createDisabledReason = "",
  
  // Content
  statsCards = [],
  children,
  error,
  
  // Loading states
  isLoading = false,
  loadingText = "Loading...",
  
  // Pagination
  currentPage,
  pageCount,
  onNextPage,
  onPrevPage,
  onPageChange,
  showPagination = false,
  
  // Additional header actions
  additionalActions = null,
  customHeaderElements = null,
  
  // Retry functionality
  showRetry = false,
  onRetry = null
}) => {
  const { isMobile, isTablet, getGridClasses, getStatsCardClasses } = useResponsive();
  
  // Desktop layout - no error rendering (handled manually in parent component)
  if (!isMobile && !isTablet) {
    return <>{children}</>;
  }
  
  // Mobile and tablet layout
  return (
    <div className="bg-black min-h-screen flex">
      <div className="flex flex-1 h-full">
        <div className={`flex-1 bg-[#0b0b0b] flex flex-col ${isTablet ? '-mt-8 pt-0' : 'pt-8'} relative`}>
          <div className="border-b border-[#333333] bg-[#0a0a0a] relative z-10">
            {/* Main header row */}
            <div className="flex justify-between items-center p-2">
              <div className="flex items-center min-w-0 flex-1">
                {PageIcon && (
                  <div className="mr-2 flex-shrink-0">
                    <PageIcon className="text-[#e0eacc]" size={18} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg font-bold text-white truncate">{pageTitle}</h1>
                </div>
              </div>
              
              <div className="flex items-center space-x-1 flex-shrink-0">
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    className="flex items-center justify-center w-8 h-8 bg-[#333333] hover:bg-[#444444] rounded text-xs transition-colors"
                    disabled={isRefreshing}
                  >
                    <FaSync size={12} className={isRefreshing ? 'animate-spin' : ''} />
                  </button>
                )}
                
                {onCreateNew && (
                  <button
                    onClick={onCreateNew}
                    className={`flex items-center justify-center w-8 h-8 rounded font-medium text-xs transition-colors ${
                      canCreate 
                        ? 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]' 
                        : 'bg-[#444444] text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!canCreate}
                    title={!canCreate ? createDisabledReason : ''}
                  >
                    <FaPlus size={12} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Secondary header row for custom elements */}
            {(customHeaderElements || additionalActions) && (
              <div className="px-1 pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    {customHeaderElements}
                    {additionalActions}
                  </div>
                  {pageDescription && (
                    <p className="text-gray-400 text-xs hidden sm:block ml-2 flex-shrink-0">{pageDescription}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Error appears after header for mobile/tablet */}
          {error && (
            <div className="px-4 pt-4">
              <ErrorMessage message={error} />
              {showRetry && onRetry && (
                <div className="mb-4 mt-4">
                  <button
                    onClick={onRetry}
                    className="w-full bg-[rgba(238,85,85,0.05)] border border-[#ff7777] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[#ff5555] hover:text-[#ff5555] hover:shadow-[inset_0_0_20px_rgba(255,119,119,0.1)] font-medium py-2 px-4 rounded-lg transition-all text-sm"
                  >
                    <div className="flex items-center justify-center">
                      <FaSync size={14} className="mr-2" />
                      <span>Retry</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div className="px-1 py-4 flex-1 flex flex-col relative">
            {statsCards.length > 0 && (
              <div className={`${getStatsCardClasses()} mb-4`}>
                {statsCards.map((card, index) => (
                  <StatsCard
                    key={index}
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    onClick={card.onClick}
                    isSelected={card.isSelected}
                    className={card.className}
                  />
                ))}
              </div>
            )}
            
            <div className="flex-1 flex flex-col relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                  <div className="w-8 h-8 border-4 border-t-[#e0eacc] border-[#e0eacc] border-opacity-20 rounded-full animate-spin"></div>
                  <p className="text-gray-400 ml-4 text-sm">{loadingText}</p>
                </div>
              )}
              <div className={`${getGridClasses()} mb-4 relative`}>
                {children}
              </div>
              
              {showPagination && (
                <MobilePagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  onNextPage={onNextPage}
                  onPrevPage={onPrevPage}
                  onPageChange={onPageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveWrapper;